/**
 * PLP Viewing Badge Component
 *
 * Displays "X people viewed" social proof on PLP product cards using
 * GA4 view counts from the BWW middleware (`/social-proof/views`).
 * The GA4 service account lives only in that worker — see
 * scripts/bww-middleware/README.md.
 *
 * Configuration comes from the Collection Container section settings
 * (published as window.__bwwSocialProofConfig by the
 * 'social-proof-views-prefetch' snippet):
 * - days:     how many days back to count views (startDate=NdaysAgo)
 * - minViews: minimum views before a badge is shown
 * - maxDisplay: display cap; counts above it render as "N+"
 * The badge wording is rendered server-side in 'plp-viewing-badge.liquid';
 * this script only fills in the count and toggles visibility.
 *
 * Performance:
 * - ONE batched request fetches view counts for the entire catalog
 *   (the middleware returns every product's count in a single cached
 *   payload) instead of one request per product on the page.
 * - The request is kicked off at HTML parse time by the prefetch snippet
 *   (window.__bwwSocialProofViewsFetch), so the response is usually ready
 *   before this script runs.
 * - Results are cached in localStorage per day-range. GA4 finalises item
 *   data on a ~24h lag and the middleware refreshes once a day, so counts
 *   only change daily; repeat PLP visits within the TTL make zero requests.
 *
 * Counts are matched to cards by product title (GA4 item_name == Shopify
 * product title). Cards without a matching row, or below the threshold,
 * keep the badge hidden.
 */

// @ts-nocheck
(() => {
  // The middleware holds the GA4 credentials; the storefront only knows this URL.
  const middlewareBase = () => String(window.BWWMiddleware?.baseUrl || "").replace(/\/+$/, "");
  const DEFAULT_DAYS = 1;
  const DEFAULT_MIN_VIEWS = 50;
  const DEFAULT_MAX_DISPLAY = 600;
  // Keep cache key prefix / TTL / request URL in sync with snippets/social-proof-views-prefetch.liquid.
  // v3 = cutover from the Acadia endpoint to the middleware; the bump discards
  // v2 entries so a returning shopper is not served the old source's cache.
  const CACHE_KEY_PREFIX = "bww:social-proof:views:v3:";
  const CACHE_TTL = 3 * 60 * 60 * 1000;

  // Console logging. DEBUG is the default, but logging can also be switched on at
  // runtime so the middleware payload can be inspected on any live page without a
  // theme push — for QA, or to debug one shopper's session:
  //   ?spdebug=1 in the URL, or
  //   localStorage.setItem('bww:debug:social-proof', '1')  (persists; remove to stop)
  const DEBUG = true;
  const DEBUG_KEY = "bww:debug:social-proof";
  const isDebug = () => {
    try {
      if (window.__bwwSocialProofDebug != null) return Boolean(window.__bwwSocialProofDebug);
      if (localStorage.getItem(DEBUG_KEY)) return true;
      if (new URLSearchParams(window.location.search).has("spdebug")) return true;
    } catch {
      // Private mode / blocked storage — fall through to the compiled default
    }
    return DEBUG;
  };
  const log = (...args) => isDebug() && console.log("[PlpViewingBadge]", ...args);

  /**
   * One structured line describing what the middleware actually returned.
   *
   * Worth its own helper because the two failure modes are indistinguishable on
   * the page — "GA4 has no row for this product" and "the request failed" both
   * just leave the badge hidden. `asOf` / `stale` also make the 24h GA4 lag
   * visible, so QA can confirm the data is yesterday's by design rather than
   * assuming it is live.
   *
   * @param {string} url the URL actually requested — see `source`
   * @param {any} data parsed response body, or null when the request failed
   * @param {number} rowCount
   * @param {string} source "prefetch" (snippet, at parse time) or "component"
   */
  const logResponse = (url, data, rowCount, source) => {
    if (!isDebug()) return;
    console.info("[PlpViewingBadge] middleware response", {
      url,
      source,
      rows: rowCount,
      asOf: data?.asOf ?? null,
      window: data?.window ?? null,
      stale: data?.stale ?? null,
      unavailable: data?.unavailable ?? false,
      generatedAt: data?.generatedAt ?? null,
      sample: Array.isArray(data?.value) ? data.value.slice(0, 3) : null,
      raw: data,
    });
  };

  const getConfig = () => {
    const cfg = window.__bwwSocialProofConfig || {};
    const days = Math.max(parseInt(cfg.days, 10) || DEFAULT_DAYS, 1);
    // Floor of 1: even with the admin threshold at 0, products with no view
    // data must never render as "0 people viewed"
    const minViews = Math.max(Number.isFinite(Number(cfg.minViews)) ? Number(cfg.minViews) : DEFAULT_MIN_VIEWS, 1);
    // Cap for the displayed number — anything above renders as "N+"
    const maxDisplay = Math.max(parseInt(cfg.maxDisplay, 10) || DEFAULT_MAX_DISPLAY, 1);
    return { days, minViews, maxDisplay };
  };

  const requestUrlFor = days => `${middlewareBase()}/social-proof/views?days=${days}`;

  const cacheKeyFor = days => `${CACHE_KEY_PREFIX}${days}d`;

  const normalizeTitle = title => (title || "").toLowerCase().replace(/\s+/g, " ").trim();

  /**
   * PLP Viewing Badge Custom Element
   * Renders a "X people viewed" message on a product card
   */
  class PlpViewingBadge extends HTMLElement {
    /**
     * @param {number} count - Number of views for this product
     * @param {number} [maxDisplay] - Cap; counts above it render as "N+"
     */
    setCount(count, maxDisplay) {
      const countEl = this.querySelector(".sp-viewing-badge__count");
      if (countEl) {
        const capped = maxDisplay && count > maxDisplay;
        const next = Number(capped ? maxDisplay : count).toLocaleString("en-US") + (capped ? "+" : "");
        if (countEl.textContent !== next) countEl.textContent = next;
      }
    }

    /**
     * Every class mutation below is guarded by a state check. `classList.add`/
     * `remove` write the class attribute even when the token set is unchanged,
     * and a MutationObserver watching `class` fires on any write — so an
     * unguarded toggle here re-triggers the grid observer and loops forever.
     */
    show() {
      this.clearLoading();
      if (this.classList.contains("hidden")) this.classList.remove("hidden");
      if (this.style.display !== "flex") this.style.display = "flex";
    }

    hide() {
      this.clearLoading();
      if (!this.classList.contains("hidden")) this.classList.add("hidden");
      if (this.style.display !== "") this.style.display = "";
    }

    /**
     * Shows the badge with an orange shimmer over the count while the
     * API data loads (same pattern as the cart loyalty points shimmer)
     */
    setLoading() {
      if (!this.classList.contains("sp-viewing-badge--loading")) {
        this.classList.add("sp-viewing-badge--loading");
      }
      if (this.classList.contains("hidden")) this.classList.remove("hidden");
      if (this.style.display !== "flex") this.style.display = "flex";
    }

    clearLoading() {
      if (this.classList.contains("sp-viewing-badge--loading")) {
        this.classList.remove("sp-viewing-badge--loading");
      }
    }
  }

  if (!customElements.get("plp-viewing-badge")) {
    customElements.define("plp-viewing-badge", PlpViewingBadge);
  }

  /**
   * PLP Viewing Badge Manager (Singleton)
   *
   * Loads the view-count map once, applies it to every badge on the page,
   * and watches the grid for cards added by load-more or facet filtering.
   */
  class PlpViewingBadgeManager {
    /**
     * @param {{days: number, minViews: number, maxDisplay: number}} config
     */
    constructor(config) {
      this.days = config.days;
      this.minViews = config.minViews;
      this.maxDisplay = config.maxDisplay;
      this._counts = null; // Map<normalized product title, view count>
      this._observer = null;
    }

    async init() {
      log("Config:", { days: this.days, minViews: this.minViews, maxDisplay: this.maxDisplay });

      // Only shimmer when a network request is actually needed — cached
      // loads apply instantly with no loading state
      if (!this._readCache()) {
        this._setLoadingAll();
      }

      this._counts = await this._loadCounts();
      if (!this._counts || this._counts.size === 0) {
        log("No view counts available, badges stay hidden");
        this._clearLoadingAll();
        return;
      }
      this.applyAll();
      this._observeGrid();
    }

    _setLoadingAll() {
      document.querySelectorAll("plp-viewing-badge[data-product-title]").forEach(badge => {
        if (typeof badge.setLoading === "function") badge.setLoading();
      });
    }

    _clearLoadingAll() {
      document.querySelectorAll("plp-viewing-badge").forEach(badge => {
        if (typeof badge.hide === "function") badge.hide();
      });
    }

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    _readCache() {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKeyFor(this.days)) || "null");
        if (cached && cached.ts && Date.now() - cached.ts < CACHE_TTL && cached.counts) {
          return cached.counts;
        }
      } catch (error) {
        // Corrupt cache — fall through to refetch
      }
      return null;
    }

    _writeCache(counts) {
      try {
        localStorage.setItem(cacheKeyFor(this.days), JSON.stringify({ ts: Date.now(), counts }));
      } catch (error) {
        // Quota exceeded / private mode — non-fatal
      }
    }

    /**
     * @returns {Promise<Map<string, number>|null>}
     */
    async _loadCounts() {
      const cached = this._readCache();
      if (cached) {
        log("Using cached counts (no request made):", Object.keys(cached).length, "products |", cached);
        return new Map(Object.entries(cached));
      }

      if (!middlewareBase()) {
        console.warn(
          "[PlpViewingBadge] window.BWWMiddleware.baseUrl is unset — check the Middleware Base URL theme setting"
        );
        return null;
      }

      try {
        const requestUrl = requestUrlFor(this.days);
        // Reuse the request started at parse time by social-proof-views-prefetch.liquid
        const pending = window.__bwwSocialProofViewsFetch;

        // Log the URL that was really fetched. When the prefetch ran, that is the
        // snippet's URL, which is built in Liquid and can differ from this one —
        // logging `requestUrl` regardless once hid a broken prefetch URL behind a
        // correct-looking log line.
        const usedUrl = pending ? window.__bwwSocialProofViewsUrl || "(prefetch, url unknown)" : requestUrl;
        const source = pending ? "prefetch" : "component";

        const data = pending ? await pending : await fetch(requestUrl).then(r => (r.ok ? r.json() : null));

        const rows = Array.isArray(data?.value) ? data.value : [];
        logResponse(usedUrl, data, rows.length, source);
        if (rows.length === 0) return null;

        const counts = {};
        for (const row of rows) {
          const views = parseInt(row.itemsViewed, 10);
          if (row.itemName && views > 0) {
            counts[normalizeTitle(row.itemName)] = views;
          }
        }
        this._writeCache(counts);
        return new Map(Object.entries(counts));
      } catch (error) {
        console.error("[PlpViewingBadge] Error fetching social proof data:", error);
        return null;
      }
    }

    /**
     * Apply counts to all unprocessed badges within root
     * @param {ParentNode} [root]
     */
    applyAll(root = document) {
      if (!this._counts) return;
      const badges = root.querySelectorAll?.("plp-viewing-badge[data-product-title]") || [];
      badges.forEach(badge => this._applyOne(badge));
    }

    _applyOne(badge) {
      if (!this._counts || typeof badge.setCount !== "function") return;

      // Idempotent enforcement: facet filtering morphs the grid and resets
      // badge markup to the server HTML (hidden), so always recompute the
      // desired state instead of tracking an "applied" flag
      const count = this._counts.get(normalizeTitle(badge.dataset.productTitle)) || 0;
      if (count > 0 && count >= this.minViews) {
        if (badge.classList.contains("hidden")) {
          log("Showing badge:", badge.dataset.productTitle, "|", count, "views");
        }
        badge.setCount(count, this.maxDisplay);
        badge.show();
      } else {
        badge.hide();
      }
    }

    /**
     * Watch for cards added by load-more, and for facet filtering re-renders
     * that morph existing badges back to their server-rendered (hidden) state
     */
    _observeGrid() {
      if (this._observer) this._observer.disconnect();

      this._observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          // Morph reset a badge's class attribute — re-enforce its state
          if (mutation.type === "attributes") {
            if (mutation.target.tagName === "PLP-VIEWING-BADGE") {
              this._applyOne(mutation.target);
            }
            continue;
          }
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches?.("plp-viewing-badge")) {
              this._applyOne(node);
            } else {
              this.applyAll(node);
            }
          }
        }

        // Discard the records our own _applyOne writes just queued. Without
        // this, re-enforcing a badge's class re-enters this callback on the
        // next microtask and the queue never drains — the main thread locks
        // up and facet filtering never finishes rendering.
        this._observer?.takeRecords();
      });

      // Scope to the results grid: observing `class` across all of document.body
      // means every loading-state toggle on the page (filter drawer, buttons,
      // facet chips) wakes this callback during filtering.
      const scope = document.querySelector("results-list") || document.body;
      this._observer.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }

  // Singleton instance
  let managerInstance = null;

  function boot() {
    const config = getConfig();

    if (managerInstance) {
      if (managerInstance.days !== config.days) {
        // Day range changed (theme editor) — needs a fresh dataset
        managerInstance.destroy();
        managerInstance = new PlpViewingBadgeManager(config);
        managerInstance.init();
      } else {
        // Section re-render replaces badge nodes, so new nodes re-evaluate
        // against the (possibly updated) threshold and cap
        managerInstance.minViews = config.minViews;
        managerInstance.maxDisplay = config.maxDisplay;
        managerInstance.applyAll();
      }
      return;
    }

    managerInstance = new PlpViewingBadgeManager(config);
    managerInstance.init();
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-initialize on Shopify section loads (theme editor)
  document.addEventListener("shopify:section:load", boot);

  /**
   * QA helper: one table of every card on the page and why its badge is or is
   * not showing. "Hidden" has three quite different causes — no GA4 row for the
   * product, a count under the threshold, and the dataset failing to load — and
   * they are indistinguishable on the page, so each row names which one applies.
   *
   *   __PlpViewingBadge__.qa()
   */
  function qa() {
    const manager = managerInstance;
    if (!manager) {
      console.warn("[PlpViewingBadge] not booted yet — run this after the page settles");
      return [];
    }
    const counts = manager._counts;
    const rows = [...document.querySelectorAll("plp-viewing-badge[data-product-title]")].map(badge => {
      const title = badge.dataset.productTitle;
      const views = counts ? counts.get(normalizeTitle(title)) : undefined;
      const visible = !badge.classList.contains("hidden");
      let reason;
      if (!counts) reason = "dataset failed to load";
      else if (views === undefined) reason = "no GA4 row for this product";
      else if (views < manager.minViews) reason = `below threshold (${manager.minViews})`;
      else reason = "shown";
      return { product: title, views: views ?? "—", threshold: manager.minViews, visible, reason };
    });
    console.table(rows);
    console.info(
      `[PlpViewingBadge] ${rows.filter(r => r.visible).length}/${rows.length} visible | ` +
        `dataset: ${counts ? counts.size : 0} products | days=${manager.days} minViews=${manager.minViews} maxDisplay=${manager.maxDisplay}`
    );
    return rows;
  }

  // Expose for debugging
  window.__PlpViewingBadge__ = {
    PlpViewingBadge,
    PlpViewingBadgeManager,
    boot,
    qa,
    getInstance: () => managerInstance,
  };
})();
